import { useState } from "react";

import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ToastProvider";
import { post } from "@/lib/api";

const PLATFORMS = ["instagram", "tiktok", "facebook", "youtube"];

export function CrosspostPanel() {
  const { push } = useToast();
  const [source, setSource] = useState("instagram");
  const [destinations, setDestinations] = useState<string[]>(["tiktok"]);
  const [assetId, setAssetId] = useState("");
  const [autoCaption, setAutoCaption] = useState(true);
  const [autoTrim, setAutoTrim] = useState(false);

  const toggleDestination = (platform: string) => {
    setDestinations((prev) =>
      prev.includes(platform) ? prev.filter((entry) => entry !== platform) : [...prev, platform],
    );
  };

  const handleCrosspost = async () => {
    if (!assetId.trim()) {
      push({ title: "Asset required", message: "Provide an asset id or URL.", variant: "destructive" });
      return;
    }
    if (!destinations.length) {
      push({ title: "Destinations missing", message: "Select at least one destination.", variant: "destructive" });
      return;
    }
    try {
      await post("/marketing/crosspost", {
        source_platform: source,
        destination_platforms: destinations,
        asset_id: assetId,
        auto_caption: autoCaption,
        auto_trim: autoTrim,
      });
      push({ title: "Crosspost queued", message: `Syncing ${assetId} to ${destinations.join(", ")}.` });
      setAssetId("");
    } catch (error) {
      push({
        title: "Unable to crosspost",
        message: error instanceof Error ? error.message : "Try again later.",
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="space-y-3 border-border/70 bg-background/40 p-4">
      <div>
        <p className="text-sm font-semibold text-foreground">Crosspost Sync</p>
        <p className="text-xs text-muted-foreground">Push posts across platforms automatically.</p>
      </div>
      <Select value={source} onValueChange={setSource}>
        <SelectTrigger className="bg-background/50 text-foreground">
          <SelectValue placeholder="Source" />
        </SelectTrigger>
        <SelectContent className="bg-background/40 text-foreground">
          {PLATFORMS.map((platform) => (
            <SelectItem key={platform} value={platform}>
              {platform}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="flex flex-wrap gap-2">
        {PLATFORMS.filter((platform) => platform !== source).map((platform) => (
          <label key={platform} className="flex items-center gap-2 text-xs text-foreground/80">
            <Checkbox
              checked={destinations.includes(platform)}
              onCheckedChange={() => toggleDestination(platform)}
            />
            {platform}
          </label>
        ))}
      </div>
      <Input
        value={assetId}
        onChange={(event) => setAssetId(event.target.value)}
        placeholder="Asset ID or URL"
        className="bg-background/40 text-foreground"
      />
      <div className="flex items-center gap-4 text-xs text-foreground/80">
        <label className="flex items-center gap-2">
          <Checkbox checked={autoCaption} onCheckedChange={(value) => setAutoCaption(Boolean(value))} /> Auto-caption
        </label>
        <label className="flex items-center gap-2">
          <Checkbox checked={autoTrim} onCheckedChange={(value) => setAutoTrim(Boolean(value))} /> Auto-trim
        </label>
      </div>
      <Button onClick={() => void handleCrosspost()}>Queue crosspost</Button>
    </Card>
  );
}
